import React, { useState, useEffect } from 'react';
import { X, Sparkle, CheckCircle, Spinner, Envelope, Copy, CaretDown, CaretUp, Lightning, Info } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/utils/api';
import { getUser } from '@/utils/auth';
import { useWorkflowStore } from '@/store/workflowStore';
import { WhatsNextMonetization } from '@/components/WhatsNext';
import OutputActions from '@/components/OutputActions';
import WhyThisFitsYouBlock from '@/components/WhyThisFitsYouBlock';

const EmailCampaignModal = ({ isOpen, onClose, onSuccess, onAction }) => {
  const user = getUser();
  const [step, setStep] = useState('input'); // 'input', 'generating', 'result'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [expandedEmail, setExpandedEmail] = useState(0);
  const [showWhatsNext, setShowWhatsNext] = useState(false);
  const [hasPrefilledFromOffer, setHasPrefilledFromOffer] = useState(false);
  
  // Zustand workflow store
  const workflowStore = useWorkflowStore();
  const { 
    offerName, 
    offerDescription, 
    audience,
    offerCreated,
    salesPageCreated,
    emailCampaignCreated,
    setEmailCampaignData
  } = workflowStore;
  
  // Workflow state derived from store
  const workflowState = {
    hasOffer: offerCreated,
    hasSalesPage: salesPageCreated,
    hasEmailCampaign: emailCampaignCreated,
    hasMonetizationPlan: false
  };
  
  const [formData, setFormData] = useState({
    campaign_type: 'launch',
    offer_name: '',
    offer_description: '',
    target_audience: '',
    num_emails: 5
  });

  // Auto-fill from workflow store when modal opens
  useEffect(() => {
    if (isOpen && !hasPrefilledFromOffer && offerCreated) {
      setFormData(prev => ({
        ...prev,
        offer_name: offerName || '',
        offer_description: offerDescription || '',
        target_audience: audience || ''
      }));
      setHasPrefilledFromOffer(true);
    }
  }, [isOpen, hasPrefilledFromOffer, offerCreated, offerName, offerDescription, audience]);

  const campaignTypes = [
    { id: 'launch', label: 'Launch', desc: 'New product launch sequence' },
    { id: 'nurture', label: 'Nurture', desc: 'Build trust over time' },
    { id: 'sales', label: 'Sales', desc: 'Direct selling sequence' },
    { id: 'welcome', label: 'Welcome', desc: 'New subscriber welcome' }
  ];

  const handleSubmit = async () => {
    if (!formData.offer_name.trim() || !formData.offer_description.trim() || !formData.target_audience.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setStep('generating');
    setLoading(true);

    try {
      const response = await api.post('/monetization/email-campaign', formData);
      setResult(response.data);
      setStep('result');
      
      // Mark email campaign as created in workflow store
      setEmailCampaignData({ id: response.data.id });
      
      if (onSuccess) onSuccess();
      toast.success('Email campaign ready!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate emails');
      setStep('input');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const copyAllEmails = () => {
    if (!result?.emails) return;
    const allContent = result.emails.map((email, i) => 
      `Email ${i + 1}: ${result.send_schedule?.[i] || ''}\n\nSubject: ${email.subject}\nPreview: ${email.preview_text}\n\n${email.body}\n\nCTA: ${email.cta}\n\n---`
    ).join('\n\n');
    navigator.clipboard.writeText(allContent);
    toast.success('All emails copied!');
  };

  const handleClose = () => {
    setStep('input');
    setResult(null);
    setShowWhatsNext(false);
    setHasPrefilledFromOffer(false);
    setFormData({
      campaign_type: 'launch',
      offer_name: '',
      offer_description: '',
      target_audience: '',
      num_emails: 5
    });
    onClose();
  };

  const handleWhatsNextAction = (action) => {
    if (onAction) {
      onAction(action);
    }
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-2xl paygbot-modal-shell bg-slate-900 rounded-2xl shadow-2xl border border-white/10"
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <Envelope size={20} className="text-white" weight="fill" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Email Campaign Generator</h2>
                <p className="text-sm text-slate-400">Create email sequences that convert</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X size={18} className="text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Input Step */}
            {step === 'input' && (
              <div className="space-y-6">
                {/* Auto-fill banner */}
                {hasPrefilledFromOffer && offerName && (
                  <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center space-x-3">
                    <Info size={18} className="text-blue-400 flex-shrink-0" />
                    <p className="text-blue-300 text-sm">
                      Using details from your offer: <span className="font-medium text-blue-400">{offerName}</span>
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Campaign Type
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {campaignTypes.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setFormData({...formData, campaign_type: type.id})}
                        className={`p-3 rounded-lg text-left transition-all ${
                          formData.campaign_type === type.id
                            ? 'bg-blue-500/20 border-blue-500/50 border'
                            : 'bg-white/5 border-white/10 border hover:bg-white/10'
                        }`}
                      >
                        <span className={`text-sm font-medium ${formData.campaign_type === type.id ? 'text-blue-400' : 'text-white'}`}>
                          {type.label}
                        </span>
                        <p className="text-xs text-slate-500 mt-1">{type.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Offer/Product Name *
                  </label>
                  <input
                    type="text"
                    value={formData.offer_name}
                    onChange={(e) => setFormData({...formData, offer_name: e.target.value})}
                    data-testid="email-offer-name"
                    placeholder="e.g., YouTube Mastery Course"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    What are you selling? *
                  </label>
                  <textarea
                    value={formData.offer_description}
                    onChange={(e) => setFormData({...formData, offer_description: e.target.value})}
                    data-testid="email-offer-desc"
                    placeholder="Brief description of your offer..."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Who is this for? *
                  </label>
                  <input
                    type="text"
                    value={formData.target_audience}
                    onChange={(e) => setFormData({...formData, target_audience: e.target.value})}
                    data-testid="email-audience"
                    placeholder="e.g., Beginners wanting to make money online"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Number of Emails: {formData.num_emails}
                  </label>
                  <input
                    type="range"
                    min="3"
                    max="7"
                    value={formData.num_emails}
                    onChange={(e) => setFormData({...formData, num_emails: parseInt(e.target.value)})}
                    className="w-full accent-blue-500"
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>3 emails</span>
                    <span>7 emails</span>
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  data-testid="email-campaign-submit"
                  className="w-full py-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold rounded-xl flex items-center justify-center space-x-2 transition-all"
                >
                  <Sparkle size={20} weight="fill" />
                  <span>Generate Email Campaign</span>
                </button>
              </div>
            )}

            {/* Generating Step */}
            {step === 'generating' && (
              <div className="py-12 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Spinner size={32} className="text-white animate-spin" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Writing Your Emails</h3>
                <p className="text-slate-400">Creating {formData.num_emails} emails for your {formData.campaign_type} campaign...</p>
              </div>
            )}

            {/* Result Step */}
            {step === 'result' && result && (
              <div className="space-y-6">
                {/* Round 92 — Why This Fits You + feedback */}
                <WhyThisFitsYouBlock
                  department="content"
                  taskType="email"
                  recommendationId={result?.id || result?.campaign_id || null}
                  recommendationSummary={result?.campaign_name ? `the "${result.campaign_name}" email campaign` : "this email campaign"}
                  businessCtx={{
                    primary_offer: result?.offer_name,
                    target_audience: result?.audience,
                  }}
                />

                <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <Envelope size={24} className="text-blue-400 mb-1" />
                      <p className="text-blue-400 font-medium">{result.campaign_name}</p>
                      <p className="text-slate-400 text-sm">{result.emails?.length || 0} emails ready</p>
                    </div>
                    <button
                      onClick={copyAllEmails}
                      className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg text-sm font-medium flex items-center space-x-2"
                    >
                      <Copy size={16} />
                      <span>Copy All</span>
                    </button>
                  </div>
                </div>

                {/* Send Schedule */}
                {result.send_schedule && (
                  <div className="flex flex-wrap gap-2">
                    {result.send_schedule.map((day, i) => (
                      <span key={i} className="px-3 py-1 bg-white/10 text-slate-300 rounded-full text-xs">
                        Email {i + 1}: {day}
                      </span>
                    ))}
                  </div>
                )}

                {/* Emails */}
                <div className="space-y-3">
                  {result.emails?.map((email, index) => (
                    <div key={index} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setExpandedEmail(expandedEmail === index ? -1 : index)}
                        className="w-full p-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                              Email {index + 1}
                            </span>
                            <span className="text-slate-500 text-xs">
                              {result.send_schedule?.[index]}
                            </span>
                          </div>
                          <p className="text-white font-medium truncate">{email.subject}</p>
                          <p className="text-slate-500 text-sm truncate">{email.preview_text}</p>
                        </div>
                        {expandedEmail === index ? (
                          <CaretUp size={20} className="text-slate-400" />
                        ) : (
                          <CaretDown size={20} className="text-slate-400" />
                        )}
                      </button>

                      {expandedEmail === index && (
                        <div className="px-4 pb-4 space-y-4 border-t border-white/10 pt-4">
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs font-medium text-slate-400 uppercase">Subject Line</label>
                              <button
                                onClick={() => handleCopy(email.subject, `subject-${index}`)}
                                className="text-blue-400 hover:text-blue-300 text-xs flex items-center space-x-1"
                              >
                                {copiedField === `subject-${index}` ? <CheckCircle size={12} /> : <Copy size={12} />}
                                <span>Copy</span>
                              </button>
                            </div>
                            <p className="text-white bg-white/5 p-2 rounded text-sm">{email.subject}</p>
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs font-medium text-slate-400 uppercase">Email Body</label>
                              <button
                                onClick={() => handleCopy(email.body, `body-${index}`)}
                                className="text-blue-400 hover:text-blue-300 text-xs flex items-center space-x-1"
                              >
                                {copiedField === `body-${index}` ? <CheckCircle size={12} /> : <Copy size={12} />}
                                <span>Copy</span>
                              </button>
                            </div>
                            <p className="text-slate-300 bg-white/5 p-3 rounded text-sm whitespace-pre-wrap">{email.body}</p>
                          </div>

                          <div>
                            <label className="text-xs font-medium text-slate-400 uppercase mb-2 block">Call to Action</label>
                            <p className="text-blue-400 font-medium bg-blue-500/10 p-2 rounded text-sm">{email.cta}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Tips */}
                {result.tips && result.tips.length > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                    <h4 className="text-amber-400 font-medium mb-2">Pro Tips</h4>
                    <ul className="space-y-1">
                      {result.tips.map((tip, i) => (
                        <li key={i} className="text-slate-400 text-sm flex items-start space-x-2">
                          <span className="text-amber-400">•</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Actions */}
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => { setStep('input'); setResult(null); setShowWhatsNext(false); }}
                    className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors"
                  >
                    Create Another
                  </button>
                  <button
                    onClick={() => setShowWhatsNext(!showWhatsNext)}
                    className={`flex-1 py-3 font-medium rounded-xl transition-colors flex items-center justify-center space-x-2 ${
                      showWhatsNext 
                        ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                        : 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'
                    }`}
                  >
                    <Lightning size={18} weight="fill" />
                    <span>{showWhatsNext ? 'Hide' : "What's Next?"}</span>
                  </button>
                </div>

                {/* What's Next Section */}
                {showWhatsNext && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4 pt-4 border-t border-white/10"
                  >
                    <WhatsNextMonetization 
                      toolUsed="email-campaign" 
                      onAction={handleWhatsNextAction} 
                      onClose={handleClose}
                      workflowState={workflowState}
                    />
                  </motion.div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default EmailCampaignModal;
